<script setup lang="ts">
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import TimePeriodV2Calendar from '@/common/components/molecules/timePeriod/TimePeriodV2Calendar.vue';
import { getLocaleLabel } from '@/common/components/molecules/timeController/utils';
import StatusTag from '@/common/components/molecules/statusTag/StatusTag.vue';
import { clickoutside as vClickOutside } from '@/common/directives/clickoutside';
import {
  setup,
  Emit,
  Props,
} from '@/common/components/molecules/rangeDateTimePicker/rangeDateTimePicker.setup';

const props = defineProps<Props>();
const emit = defineEmits<Emit>();

const {
  t,

  readonlyTimes,
  dateTimePeriod,
  selectedDateString,
  periodTagLabel,

  pastInputTime,
  pastInputError,
  pastInputUnit,
  filteredTimeUnitItems,
  onInputValidation,
  onClickInputTimeOk,

  recentlySearches,
  onClickRecentlySearch,
  deleteRecentlySearch,

  filteredQuickPeriods,
  onClickQuickPeriod,

  onClickCalendarOk,

  timeCalendarRef,
  dateTimePickerInputRef,
  dateTimePickerPopupRef,
  isShowQuickDatePopup,
  popupWrapperStyle,
  showCalendar,
  closeCalendar,
  openCalendar,
  closeAll,
  togglePopup,
} = setup(props, emit);
</script>

<template>
  <div class="range-date-time-picker">
    <div
      ref="dateTimePickerInputRef"
      :class="['range-date-time-picker__input', { error: props.isError }]"
    >
      <ev-text-field
        v-model="selectedDateString"
        :title="selectedDateString"
        type="text"
        :placeholder="props.placeholder || ''"
        readonly
        :error-msg="props.errorMessage"
        :clearable="props.clearable"
        :disabled="props.disabled"
        @click="togglePopup"
      >
        <template #icon-prefix>
          <status-tag
            v-if="props.showPeriodTag"
            type="disabled"
            class="range-date-time-picker__period-tag"
          >
            {{ periodTagLabel }}
          </status-tag>
          <ev-icon
            v-else-if="!props.hidePrefixIcon"
            icon="ev-icon-calendar"
          />
        </template>
        <template #icon-suffix>
          <div class="ev-select suffix-area">
            <span class="ev-input-suffix">
              <i
                class="ev-input-suffix-arrow ev-icon-s-arrow-down"
                :class="{
                  selected: isShowQuickDatePopup,
                }"
              />
            </span>
          </div>
        </template>
      </ev-text-field>
    </div>
    <teleport
      to="#popup"
      defer
    >
      <div
        v-click-outside="closeAll"
        :style="popupWrapperStyle"
        class="range-date-time-picker__popup"
      >
        <div
          v-if="isShowQuickDatePopup"
          ref="dateTimePickerPopupRef"
          class="range-date-picker__popup-contents"
        >
          <div
            v-if="!props.hideInput"
            class="range-date-time-picker__input-section"
          >
            <label class="range-date-time-picker__section-title">
              {{ t('WORD.PAST', 'Past') }}
            </label>
            <div class="range-date-time-picker__input-wrapper">
              <ev-text-field
                v-model="pastInputTime"
                type="text"
                :error-msg="pastInputError"
                @input="(val) => onInputValidation(val)"
              />
              <ev-select
                v-model="pastInputUnit"
                :items="filteredTimeUnitItems"
              />
            </div>
            <base-button
              type="primary"
              size="small"
              :disabled="pastInputError !== ''"
              @click.stop="() => onClickInputTimeOk(pastInputTime, pastInputUnit)"
            >
              <span>{{ t('WORD.OK', 'OK') }}</span>
            </base-button>
          </div>

          <div
            v-if="recentlySearches?.length"
            class="range-date-time-picker__recently-section"
          >
            <label class="range-date-time-picker__section-title">
              {{ t('WORD.RECENT', 'Recent') }}
            </label>
            <ul>
              <li
                v-for="(searchItem, index) in recentlySearches"
                :key="searchItem.id"
                class="range-date-time-picker__list-item"
                @click="() => onClickRecentlySearch(searchItem.value)"
              >
                <span>{{ getLocaleLabel(searchItem.value.label) }}</span>
                <base-button
                  type="icon"
                  size="xsmall"
                  @click.stop="() => deleteRecentlySearch(index)"
                >
                  <ev-icon icon="icon-close" />
                </base-button>
              </li>
            </ul>
          </div>

          <div class="range-date-time-picker__quick-section">
            <label class="range-date-time-picker__section-title">
              {{ t('WORD.QUICK_RANGES', 'Quick Ranges') }}
            </label>
            <div class="range-date-time-picker__quick-section-contents">
              <base-button
                v-for="quickPeriod in filteredQuickPeriods"
                :key="quickPeriod.value"
                tag="button"
                @click.stop="() => onClickQuickPeriod(quickPeriod)"
              >
                {{ quickPeriod.name }}
              </base-button>
            </div>
            <base-button
              shape="border"
              class="range-date-time-picker__calendar-button"
              @click.stop="openCalendar"
            >
              <ev-icon icon="icon-calendar" />
              <span>{{ t('WORD.SELECT_FROM_CALENDAR', 'Select from calendar') }}</span>
            </base-button>
          </div>
        </div>

        <time-period-v2-calendar
          v-if="showCalendar"
          ref="timeCalendarRef"
          :date-time-period="dateTimePeriod"
          allow-time
          :max-range-in-sec="props.maxRangeInSec"
          :min-range-in-sec="props.minRangeInSec"
          :minimum-interval-in-sec="props.minimumIntervalInSec"
          :readonly-times="readonlyTimes"
          :allow-future-dates="props.allowFutureDates"
          :time-unit="props.timeUnit"
          @click-ok="onClickCalendarOk"
          @click-cancel="closeCalendar"
        />
      </div>
    </teleport>
  </div>
</template>
<style lang="scss" scoped>
.range-date-time-picker {
  width: 100%;
  max-width: 100%;
}
.range-date-time-picker__input {
  :deep(.ev-text-field) {
    width: 100% !important;
  }

  :deep(.ev-input) {
    border-color: var(--input-text-border-color) !important;
    background-color: transparent !important;
    color: var(--main-font-color) !important;

    &:hover {
      border-color: var(--input-text-hover-border-color) !important;
    }
    &:focus {
      border-color: var(--input-text-active-border-color) !important;
      color: var(--main-font-color) !important;
    }
  }

  &.error {
    :deep(.ev-text-field .ev-input.ev-input.ev-input) {
      border-color: var(--color-red-05-05) !important;
    }
  }

  .suffix-area {
    position: absolute;
    right: -8px;
    pointer-events: none;
  }

  :deep(.ev-input-prefix) {
    min-width: 16px;
  }
}
.range-date-time-picker__period-tag.status-tag {
  min-width: 35px;
}
.range-date-time-picker__popup {
  display: inline-flex;
  position: fixed;
  z-index: $on-popup;
  gap: 2px;
}
.range-date-picker__popup-contents {
  flex: 100% 0 0;
  min-width: 320px;
  max-width: 320px;
  height: fit-content;
  padding: 0 10px;
  background-color: var(--color-gray-07-00);
  box-shadow: var(--range-date-time-picker-box-shadow);
}
.range-date-time-picker__section-title {
  padding: 0 6px;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 700;
  color: var(--main-font-color);
}
.range-date-time-picker__input-section {
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  width: 100%;
  padding: 16px 0;
  border-bottom: 1px solid var(--color-gray-05-05);

  :deep(.ev-text-field),
  :deep(.ev-select) {
    width: auto !important;
  }
  :deep(.ev-text-field) {
    .ev-input {
      cursor: text;
    }
  }
  :deep(.ev-text-field-error) {
    position: absolute;
    white-space: nowrap;
  }
}
.range-date-time-picker__input-wrapper {
  display: flex;
  flex: 1;
  flex-direction: row;
  gap: 4px;
  align-items: center;
}
.range-date-time-picker__recently-section {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px 0;
  border-bottom: 1px solid var(--color-gray-05-05);

  .range-date-time-picker__list-item {
    display: flex;
    flex-direction: row;
    gap: 3px;
    align-items: center;
    height: 23px;
    padding: 0 6px;
    cursor: pointer;
    border-radius: 2px;
    font-size: 12px;
    font-weight: 400;
    line-height: 23px;
    color: var(--color-gray-02-10);

    span {
      flex: auto;

      @include shortening();
    }

    &:hover {
      background-color: var(--color-blue-05-00);
    }
  }
}
.range-date-time-picker__quick-section {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px 0;

  .range-date-time-picker__quick-section-contents {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1px;
    width: 100%;
    padding: 0 0 16px;
  }

  button {
    height: 23px;
    padding: 3px 6px;
    cursor: pointer;
    border-radius: 2px;
    background-color: transparent;
    color: var(--color-gray-02-10);
    text-align: left !important;
    white-space: nowrap;

    &:hover {
      background-color: var(--color-blue-05-00);
    }
  }
}
.range-date-time-picker__calendar-button {
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: center;
  width: 100%;
}
.time-period-v2-calendar {
  position: relative;
  right: 2px;
  left: 2px;
  height: fit-content;
}
</style>
